/**
 * Traffic Creator Blog — Custom SVG Icon System
 *
 * Design language:
 *  - Primary accent: #ff5a0a (brand orange)
 *  - Secondary dark: #0f172a (navy)
 *  - Style: clean geometric, modern SaaS, tech-forward
 *  - Sizes: default 24×24, but accept width/height overrides
 *
 * Usage in blog HTML (blogContent.ts):
 *   Instead of ✅  → <span class="tc-icon tc-icon-check"></span>
 *   Instead of ❌  → <span class="tc-icon tc-icon-cross"></span>
 *   Instead of 🎯  → <span class="tc-icon tc-icon-target"></span>
 *   ... etc.
 *
 * The CSS class approach lets us use these inside dangerouslySetInnerHTML content.
 * We inject the icon styles via a <style> block rendered once in the Blog component.
 */

import React from 'react';

// ─── Props ──────────────────────────────────────────────
interface IconProps {
    size?: number;
    className?: string;
    color?: string;
    secondaryColor?: string;
}

const defaults = {
    size: 24,
    color: '#ff5a0a',
    secondary: '#0f172a',
};

// ─── Check / Success ────────────────────────────────────
export const IconCheck: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <circle cx="12" cy="12" r="11" fill={color} opacity="0.12" />
        <circle cx="12" cy="12" r="11" stroke={color} strokeWidth="1.5" />
        <path d="M7.5 12.5L10.5 15.5L16.5 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ─── Cross / Error ──────────────────────────────────────
export const IconCross: React.FC<IconProps> = ({
    size = defaults.size,
    color = '#ef4444',
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <circle cx="12" cy="12" r="11" fill={color} opacity="0.12" />
        <circle cx="12" cy="12" r="11" stroke={color} strokeWidth="1.5" />
        <path d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// ─── Target / Verdict ───────────────────────────────────
export const IconTarget: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5" />
        <circle cx="12" cy="12" r="6" stroke={color} strokeWidth="1.5" />
        <circle cx="12" cy="12" r="2.5" fill={color} />
        <path d="M12 2V5M12 19V22M2 12H5M19 12H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

// ─── Warning / Alert ────────────────────────────────────
export const IconWarning: React.FC<IconProps> = ({
    size = defaults.size,
    color = '#f59e0b',
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M12 3L2 21H22L12 3Z" fill={color} opacity="0.12" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M12 10V14" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="17" r="1" fill={color} />
    </svg>
);

// ─── Lightning / Speed ──────────────────────────────────
export const IconLightning: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M13 2L4.09 12.63C3.74 13.04 4.03 13.67 4.56 13.67H11L10 22L18.91 11.37C19.26 10.96 18.97 10.33 18.44 10.33H12L13 2Z"
            fill={color} opacity="0.15" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

// ─── Search / Magnifier ─────────────────────────────────
export const IconSearch: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <circle cx="10.5" cy="10.5" r="7" stroke={color} strokeWidth="1.5" />
        <path d="M15.5 15.5L21 21" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <circle cx="10.5" cy="10.5" r="3" fill={color} opacity="0.12" />
    </svg>
);

// ─── Lightbulb / Tip ────────────────────────────────────
export const IconTip: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M9 21H15M9 18H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        <path d="M12 2C8.13 2 5 5.13 5 9C5 11.38 6.19 13.47 8 14.74V17C8 17.55 8.45 18 9 18H15C15.55 18 16 17.55 16 17V14.74C17.81 13.47 19 11.38 19 9C19 5.13 15.87 2 12 2Z"
            fill={color} opacity="0.12" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M12 6V10L14 12" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ─── Rocket / Launch ────────────────────────────────────
export const IconRocket: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M4.5 16.5C3.5 17.5 3 21 3 21S6.5 20.5 7.5 19.5C8.17 18.83 8.17 17.76 7.5 17.09C6.83 16.42 5.76 16.42 4.5 16.5Z" fill={color} opacity="0.2" />
        <path d="M12 15L9 12L12.22 3.12C12.35 2.76 12.79 2.6 13.12 2.82L20.5 8L15 12" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M9 12L4 17M15 12L12 15" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="16" cy="6.5" r="1.5" fill={color} />
    </svg>
);

// ─── Shield / Safety ────────────────────────────────────
export const IconShield: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M12 2L4 6V12C4 16.42 7.4 20.54 12 22C16.6 20.54 20 16.42 20 12V6L12 2Z"
            fill={color} opacity="0.1" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M9 12L11 14L15 10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ─── Chart / Analytics ──────────────────────────────────
export const IconChart: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <rect x="3" y="14" width="4" height="7" rx="1" fill={color} opacity="0.3" />
        <rect x="10" y="8" width="4" height="13" rx="1" fill={color} opacity="0.5" />
        <rect x="17" y="3" width="4" height="18" rx="1" fill={color} />
        <path d="M2 21H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

// ─── Globe / Global ─────────────────────────────────────
export const IconGlobe: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5" />
        <ellipse cx="12" cy="12" rx="4" ry="10" stroke={color} strokeWidth="1.5" />
        <path d="M2 12H22" stroke={color} strokeWidth="1.5" />
        <path d="M4 7H20M4 17H20" stroke={color} strokeWidth="1" opacity="0.5" />
    </svg>
);

// ─── Lock / Security ────────────────────────────────────
export const IconLock: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <rect x="5" y="11" width="14" height="10" rx="2" fill={color} opacity="0.12" stroke={color} strokeWidth="1.5" />
        <path d="M8 11V7C8 4.79 9.79 3 12 3C14.21 3 16 4.79 16 7V11" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="16" r="1.5" fill={color} />
    </svg>
);

// ─── Trophy / Winner ────────────────────────────────────
export const IconTrophy: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M8 21H16M12 17V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        <path d="M7 4H17V10C17 13.31 14.76 16 12 16C9.24 16 7 13.31 7 10V4Z"
            fill={color} opacity="0.12" stroke={color} strokeWidth="1.5" />
        <path d="M7 7H4C4 10 5.5 12 7 12M17 7H20C20 10 18.5 12 17 12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        <path d="M7 4H17" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

// ─── Dollar / Money ─────────────────────────────────────
export const IconMoney: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <circle cx="12" cy="12" r="10" fill={color} opacity="0.08" stroke={color} strokeWidth="1.5" />
        <path d="M12 6V18M15 9.5C15 8.12 13.66 7 12 7C10.34 7 9 8.12 9 9.5C9 10.88 10.34 12 12 12C13.66 12 15 13.12 15 14.5C15 15.88 13.66 17 12 17C10.34 17 9 15.88 9 14.5"
            stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

// ─── Pin / Important ────────────────────────────────────
export const IconPin: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z"
            fill={color} opacity="0.12" stroke={color} strokeWidth="1.5" />
        <circle cx="12" cy="9" r="3" fill={color} />
    </svg>
);

// ─── Star / Rating ──────────────────────────────────────
export const IconStar: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M12 2L14.09 8.26L21 9.27L16 14.14L17.18 21.02L12 17.77L6.82 21.02L8 14.14L3 9.27L9.91 8.26L12 2Z"
            fill={color} opacity="0.2" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

// ─── Fire / Hot / Trending ──────────────────────────────
export const IconFire: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M12 2C9.24 6 6 9 6 14C6 17.31 8.69 20 12 20C15.31 20 18 17.31 18 14C18 9 14.76 6 12 2Z"
            fill={color} opacity="0.15" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M12 20C10.34 20 9 18.66 9 17C9 15 11 13 12 12C13 13 15 15 15 17C15 18.66 13.66 20 12 20Z"
            fill={color} opacity="0.4" />
    </svg>
);

// ─── Clipboard / Checklist ──────────────────────────────
export const IconClipboard: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <rect x="5" y="4" width="14" height="17" rx="2" fill={color} opacity="0.08" stroke={color} strokeWidth="1.5" />
        <path d="M9 2H15V4H9V2Z" fill={color} opacity="0.3" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M9 11L11 13L15 9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 17H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

// ─── Traffic / Network Signal ───────────────────────────
export const IconTraffic: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M3 18L8 13L12 16L16 10L21 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 6H21V10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 18L8 13L12 16L16 10L21 6" fill={color} opacity="0.08" />
    </svg>
);

// ─── SEO / Search Engine ────────────────────────────────
export const IconSeo: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <rect x="3" y="3" width="18" height="14" rx="2" fill={color} opacity="0.08" stroke={color} strokeWidth="1.5" />
        <path d="M7 8H17M7 11H13" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="16" cy="18" r="3" stroke={color} strokeWidth="1.5" />
        <path d="M18.5 20.5L21 23" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

// ─── Bot / Robot ────────────────────────────────────────
export const IconBot: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <rect x="4" y="8" width="16" height="12" rx="3" fill={color} opacity="0.1" stroke={color} strokeWidth="1.5" />
        <circle cx="9" cy="14" r="1.5" fill={color} />
        <circle cx="15" cy="14" r="1.5" fill={color} />
        <path d="M12 4V8" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="3" r="1.5" fill={color} opacity="0.4" stroke={color} strokeWidth="1" />
        <path d="M1 14H4M20 14H23" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

// ─── Proxy / Network ────────────────────────────────────
export const IconProxy: React.FC<IconProps> = ({
    size = defaults.size,
    color = defaults.color,
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <circle cx="12" cy="12" r="3" fill={color} opacity="0.3" stroke={color} strokeWidth="1.5" />
        <circle cx="5" cy="5" r="2" stroke={color} strokeWidth="1.5" />
        <circle cx="19" cy="5" r="2" stroke={color} strokeWidth="1.5" />
        <circle cx="5" cy="19" r="2" stroke={color} strokeWidth="1.5" />
        <circle cx="19" cy="19" r="2" stroke={color} strokeWidth="1.5" />
        <path d="M7 7L10 10M14 10L17 7M7 17L10 14M14 14L17 17" stroke={color} strokeWidth="1.5" />
    </svg>
);

// ─── Info / Note ────────────────────────────────────────
export const IconInfo: React.FC<IconProps> = ({
    size = defaults.size,
    color = '#3b82f6',
    className,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
        <circle cx="12" cy="12" r="10" fill={color} opacity="0.1" stroke={color} strokeWidth="1.5" />
        <path d="M12 16V12" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="8" r="1" fill={color} />
    </svg>
);


// ═══════════════════════════════════════════════════════════
// CSS-based icon system for use inside dangerouslySetInnerHTML
// ═══════════════════════════════════════════════════════════

/**
 * This CSS string must be injected once into the page (e.g. via <style>)
 * so that blog content HTML can use <span class="tc-icon tc-icon-check">
 * instead of raw emojis like ✅.
 *
 * Each icon is a 20×20 inline SVG encoded as a CSS background-image.
 */
export const BLOG_ICON_CSS = `
/* ─── Traffic Creator Blog Icon System ─── */
.tc-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  vertical-align: middle;
  margin-right: 6px;
  flex-shrink: 0;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

/* Check / Pros */
.tc-icon-check {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Ccircle cx='12' cy='12' r='11' fill='%23ff5a0a' opacity='.12'/%3E%3Ccircle cx='12' cy='12' r='11' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M7.5 12.5L10.5 15.5L16.5 9' stroke='%23ff5a0a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}

/* Cross / Cons */
.tc-icon-cross {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Ccircle cx='12' cy='12' r='11' fill='%23ef4444' opacity='.12'/%3E%3Ccircle cx='12' cy='12' r='11' stroke='%23ef4444' stroke-width='1.5'/%3E%3Cpath d='M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5' stroke='%23ef4444' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
}

/* Target / Verdict */
.tc-icon-target {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Ccircle cx='12' cy='12' r='10' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Ccircle cx='12' cy='12' r='6' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Ccircle cx='12' cy='12' r='2.5' fill='%23ff5a0a'/%3E%3Cpath d='M12 2V5M12 19V22M2 12H5M19 12H22' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
}

/* Warning */
.tc-icon-warning {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M12 3L2 21H22L12 3Z' fill='%23f59e0b' opacity='.12' stroke='%23f59e0b' stroke-width='1.5' stroke-linejoin='round'/%3E%3Cpath d='M12 10V14' stroke='%23f59e0b' stroke-width='2' stroke-linecap='round'/%3E%3Ccircle cx='12' cy='17' r='1' fill='%23f59e0b'/%3E%3C/svg%3E");
}

/* Lightning / Speed */
.tc-icon-lightning {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M13 2L4.09 12.63C3.74 13.04 4.03 13.67 4.56 13.67H11L10 22L18.91 11.37C19.26 10.96 18.97 10.33 18.44 10.33H12L13 2Z' fill='%23ff5a0a' opacity='.15' stroke='%23ff5a0a' stroke-width='1.5' stroke-linejoin='round'/%3E%3C/svg%3E");
}

/* Search */
.tc-icon-search {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Ccircle cx='10.5' cy='10.5' r='7' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M15.5 15.5L21 21' stroke='%23ff5a0a' stroke-width='2' stroke-linecap='round'/%3E%3Ccircle cx='10.5' cy='10.5' r='3' fill='%23ff5a0a' opacity='.12'/%3E%3C/svg%3E");
}

/* Tip / Lightbulb */
.tc-icon-tip {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M9 21H15M9 18H15' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9C5 11.38 6.19 13.47 8 14.74V17C8 17.55 8.45 18 9 18H15C15.55 18 16 17.55 16 17V14.74C17.81 13.47 19 11.38 19 9C19 5.13 15.87 2 12 2Z' fill='%23ff5a0a' opacity='.12' stroke='%23ff5a0a' stroke-width='1.5' stroke-linejoin='round'/%3E%3C/svg%3E");
}

/* Rocket */
.tc-icon-rocket {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M4.5 16.5C3.5 17.5 3 21 3 21S6.5 20.5 7.5 19.5C8.17 18.83 8.17 17.76 7.5 17.09C6.83 16.42 5.76 16.42 4.5 16.5Z' fill='%23ff5a0a' opacity='.2'/%3E%3Cpath d='M12 15L9 12L12.22 3.12C12.35 2.76 12.79 2.6 13.12 2.82L20.5 8L15 12' stroke='%23ff5a0a' stroke-width='1.5' stroke-linejoin='round'/%3E%3Cpath d='M9 12L4 17M15 12L12 15' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3Ccircle cx='16' cy='6.5' r='1.5' fill='%23ff5a0a'/%3E%3C/svg%3E");
}

/* Shield / Safety */
.tc-icon-shield {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M12 2L4 6V12C4 16.42 7.4 20.54 12 22C16.6 20.54 20 16.42 20 12V6L12 2Z' fill='%23ff5a0a' opacity='.1' stroke='%23ff5a0a' stroke-width='1.5' stroke-linejoin='round'/%3E%3Cpath d='M9 12L11 14L15 10' stroke='%23ff5a0a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}

/* Chart / Analytics */
.tc-icon-chart {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Crect x='3' y='14' width='4' height='7' rx='1' fill='%23ff5a0a' opacity='.3'/%3E%3Crect x='10' y='8' width='4' height='13' rx='1' fill='%23ff5a0a' opacity='.5'/%3E%3Crect x='17' y='3' width='4' height='18' rx='1' fill='%23ff5a0a'/%3E%3Cpath d='M2 21H22' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
}

/* Globe */
.tc-icon-globe {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Ccircle cx='12' cy='12' r='10' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cellipse cx='12' cy='12' rx='4' ry='10' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M2 12H22' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3C/svg%3E");
}

/* Lock */
.tc-icon-lock {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Crect x='5' y='11' width='14' height='10' rx='2' fill='%23ff5a0a' opacity='.12' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M8 11V7C8 4.79 9.79 3 12 3C14.21 3 16 4.79 16 7V11' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3Ccircle cx='12' cy='16' r='1.5' fill='%23ff5a0a'/%3E%3C/svg%3E");
}

/* Trophy */
.tc-icon-trophy {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M8 21H16M12 17V21' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3Cpath d='M7 4H17V10C17 13.31 14.76 16 12 16C9.24 16 7 13.31 7 10V4Z' fill='%23ff5a0a' opacity='.12' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M7 7H4C4 10 5.5 12 7 12M17 7H20C20 10 18.5 12 17 12' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
}

/* Money */
.tc-icon-money {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Ccircle cx='12' cy='12' r='10' fill='%23ff5a0a' opacity='.08' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M12 6V18M15 9.5C15 8.12 13.66 7 12 7C10.34 7 9 8.12 9 9.5C9 10.88 10.34 12 12 12C13.66 12 15 13.12 15 14.5C15 15.88 13.66 17 12 17C10.34 17 9 15.88 9 14.5' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
}

/* Star */
.tc-icon-star {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M12 2L14.09 8.26L21 9.27L16 14.14L17.18 21.02L12 17.77L6.82 21.02L8 14.14L3 9.27L9.91 8.26L12 2Z' fill='%23ff5a0a' opacity='.2' stroke='%23ff5a0a' stroke-width='1.5' stroke-linejoin='round'/%3E%3C/svg%3E");
}

/* Fire / Trending */
.tc-icon-fire {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M12 2C9.24 6 6 9 6 14C6 17.31 8.69 20 12 20C15.31 20 18 17.31 18 14C18 9 14.76 6 12 2Z' fill='%23ff5a0a' opacity='.15' stroke='%23ff5a0a' stroke-width='1.5' stroke-linejoin='round'/%3E%3Cpath d='M12 20C10.34 20 9 18.66 9 17C9 15 11 13 12 12C13 13 15 15 15 17C15 18.66 13.66 20 12 20Z' fill='%23ff5a0a' opacity='.4'/%3E%3C/svg%3E");
}

/* Clipboard / Checklist */
.tc-icon-clipboard {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Crect x='5' y='4' width='14' height='17' rx='2' fill='%23ff5a0a' opacity='.08' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M9 2H15V4H9V2Z' fill='%23ff5a0a' opacity='.3' stroke='%23ff5a0a' stroke-width='1.5' stroke-linejoin='round'/%3E%3Cpath d='M9 11L11 13L15 9' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M9 17H15' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
}

/* Bot / Robot */
.tc-icon-bot {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Crect x='4' y='8' width='16' height='12' rx='3' fill='%23ff5a0a' opacity='.1' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Ccircle cx='9' cy='14' r='1.5' fill='%23ff5a0a'/%3E%3Ccircle cx='15' cy='14' r='1.5' fill='%23ff5a0a'/%3E%3Cpath d='M12 4V8' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3Ccircle cx='12' cy='3' r='1.5' fill='%23ff5a0a' opacity='.4' stroke='%23ff5a0a' stroke-width='1'/%3E%3Cpath d='M1 14H4M20 14H23' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
}

/* Traffic / Growth */
.tc-icon-traffic {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M3 18L8 13L12 16L16 10L21 6' stroke='%23ff5a0a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M17 6H21V10' stroke='%23ff5a0a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}

/* SEO */
.tc-icon-seo {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Crect x='3' y='3' width='18' height='14' rx='2' fill='%23ff5a0a' opacity='.08' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M7 8H17M7 11H13' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3Ccircle cx='16' cy='18' r='3' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M18.5 20.5L21 23' stroke='%23ff5a0a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
}

/* Info / Note */
.tc-icon-info {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Ccircle cx='12' cy='12' r='10' fill='%233b82f6' opacity='.1' stroke='%233b82f6' stroke-width='1.5'/%3E%3Cpath d='M12 16V12' stroke='%233b82f6' stroke-width='2' stroke-linecap='round'/%3E%3Ccircle cx='12' cy='8' r='1' fill='%233b82f6'/%3E%3C/svg%3E");
}

/* Proxy / Network */
.tc-icon-proxy {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Ccircle cx='12' cy='12' r='3' fill='%23ff5a0a' opacity='.3' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Ccircle cx='5' cy='5' r='2' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Ccircle cx='19' cy='5' r='2' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Ccircle cx='5' cy='19' r='2' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Ccircle cx='19' cy='19' r='2' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Cpath d='M7 7L10 10M14 10L17 7M7 17L10 14M14 14L17 17' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3C/svg%3E");
}

/* Pin / Location */
.tc-icon-pin {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z' fill='%23ff5a0a' opacity='.12' stroke='%23ff5a0a' stroke-width='1.5'/%3E%3Ccircle cx='12' cy='9' r='3' fill='%23ff5a0a'/%3E%3C/svg%3E");
}
`;

// ─── Quick reference: emoji → CSS class mapping ─────────
export const EMOJI_TO_ICON_CLASS: Record<string, string> = {
    '✅': 'tc-icon tc-icon-check',
    '❌': 'tc-icon tc-icon-cross',
    '🎯': 'tc-icon tc-icon-target',
    '⚠️': 'tc-icon tc-icon-warning',
    '⚠': 'tc-icon tc-icon-warning',
    '⚡': 'tc-icon tc-icon-lightning',
    '⚡️': 'tc-icon tc-icon-lightning',
    '🔍': 'tc-icon tc-icon-search',
    '💡': 'tc-icon tc-icon-tip',
    '🚀': 'tc-icon tc-icon-rocket',
    '🛡️': 'tc-icon tc-icon-shield',
    '🛡': 'tc-icon tc-icon-shield',
    '📊': 'tc-icon tc-icon-chart',
    '📈': 'tc-icon tc-icon-traffic',
    '🏆': 'tc-icon tc-icon-trophy',
    '💰': 'tc-icon tc-icon-money',
    '🌐': 'tc-icon tc-icon-globe',
    '🔒': 'tc-icon tc-icon-lock',
    '📌': 'tc-icon tc-icon-pin',
    '⭐': 'tc-icon tc-icon-star',
    '⭐️': 'tc-icon tc-icon-star',
    '🔥': 'tc-icon tc-icon-fire',
    '📋': 'tc-icon tc-icon-clipboard',
    '🤖': 'tc-icon tc-icon-bot',
    'ℹ️': 'tc-icon tc-icon-info',
};

/**
 * Replaces all Unicode emojis in HTML content with branded SVG icon spans.
 * Use this when rendering blog content via dangerouslySetInnerHTML.
 */
export function replaceEmojisWithIcons(html: string): string {
    let result = html;
    for (const [emoji, cssClass] of Object.entries(EMOJI_TO_ICON_CLASS)) {
        // Only replace emoji that appears outside of HTML tags
        result = result.replaceAll(emoji, `<span class="${cssClass}"></span>`);
    }
    return result;
}
